// Staff customer list; owners also get history, staff note, block/unblock and duplicate merging.
import { useCallback, useEffect, useState } from 'react';
import type { CustomerRow, StaffBooking } from '../api/types.ts';
import { useShop } from '../app/boot.tsx';
import { StaffTabs } from '../app/nav.tsx';
import { fieldErrors, message } from '../lib/errors.ts';
import { thaiDateTime } from '../lib/time.ts';
import { Button, Card, Chip, Dialog, Empty, Field, Loading, Page, Sheet, useToast } from '../ui/index.tsx';
import { STATUS_LABEL } from './status.ts';

export function Customers() {
  const { api, isOwner } = useShop();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<CustomerRow[] | null>(null);
  const [open, setOpen] = useState<CustomerRow | null>(null);
  const [matches, setMatches] = useState<{ lineCustomer: CustomerRow; phoneCustomer: CustomerRow }[]>([]);
  const [merging, setMerging] = useState<{ lineCustomer: CustomerRow; phoneCustomer: CustomerRow } | null>(null);
  const [busy, setBusy] = useState(false);

  const search = useCallback(async (term: string) => {
    try { setRows(await api.staff.customers(term)); } catch (e) { toast(message(e), 'error'); setRows([]); }
  }, [api, toast]);

  const loadMatches = useCallback(async () => {
    if (!isOwner) return;
    try { setMatches(await api.staff.matches()); } catch { setMatches([]); }
  }, [api, isOwner]);

  useEffect(() => {
    const t = setTimeout(() => void search(q.trim()), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [q, search]);
  useEffect(() => { void loadMatches(); }, [loadMatches]);

  async function merge() {
    if (!merging) return;
    setBusy(true);
    try {
      await api.staff.merge(merging.phoneCustomer.id, merging.lineCustomer.id);
      toast('รวมข้อมูลลูกค้าแล้ว', 'success');
      setMerging(null);
      await Promise.all([loadMatches(), search(q.trim())]);
    } catch (e) {
      toast(message(e), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Page title="ลูกค้า">
        <Field label="ค้นหาลูกค้า (ชื่อหรือเบอร์)">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="เช่น สมชาย หรือ 081" />
        </Field>
        {isOwner && matches.length > 0 && (
          <>
            <p className="section-title">อาจเป็นลูกค้าคนเดียวกัน</p>
            <div className="list" style={{ marginBottom: 12 }}>
              {matches.map((m) => (
                <Card key={`${m.lineCustomer.id}-${m.phoneCustomer.id}`}>
                  <p><b>{m.phoneCustomer.fullName}</b> (จองทางโทรศัพท์) กับ <b>{m.lineCustomer.fullName}</b> (LINE)</p>
                  <p className="muted small">เบอร์เดียวกัน {m.lineCustomer.phone}</p>
                  <div style={{ marginTop: 8 }}><Button variant="secondary" className="btn-sm" onClick={() => setMerging(m)}>รวมข้อมูล</Button></div>
                </Card>
              ))}
            </div>
          </>
        )}
        {rows === null ? <Loading /> : rows.length === 0 ? <Empty title="ไม่พบลูกค้า" /> : (
          <div className="list">
            {rows.map((r) => (
              <Card key={r.id} as="button" onClick={() => setOpen(r)}>
                <div className="row-between">
                  <div className="card-title">{r.fullName}</div>
                  {r.blocked && <Chip tone="accent">ระงับ</Chip>}
                </div>
                <div className="muted small">{r.phone ?? 'ไม่มีเบอร์'} · {r.lineUserId ? 'มี LINE' : 'ไม่มี LINE'}{r.noShowCount ? ` · เคยไม่มา ${r.noShowCount} ครั้ง` : ''}</div>
              </Card>
            ))}
          </div>
        )}
      </Page>
      {open && <CustomerSheet customer={open} onClose={() => setOpen(null)} onChanged={(c) => { setOpen(c); void search(q.trim()); }} />}
      <Dialog open={merging !== null} title="รวมข้อมูลลูกค้า?" confirmLabel="ยืนยันรวม" loading={busy} onConfirm={() => void merge()} onCancel={() => setMerging(null)}>
        {merging && <p>ประวัติการจองของ “{merging.phoneCustomer.fullName}” จะย้ายไปอยู่กับ “{merging.lineCustomer.fullName}” (LINE) และลบรายการซ้ำ</p>}
      </Dialog>
      <StaffTabs owner={isOwner} />
    </>
  );
}

function CustomerSheet({ customer, onClose, onChanged }: { customer: CustomerRow; onClose: () => void; onChanged: (c: CustomerRow) => void }) {
  const { api, isOwner } = useShop();
  const toast = useToast();
  const [history, setHistory] = useState<StaffBooking[] | null>(null);
  const [note, setNote] = useState(customer.staffNote ?? '');
  const [noteError, setNoteError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOwner) return;
    api.staff.customerHistory(customer.id).then(setHistory).catch(() => setHistory([]));
  }, [api, customer.id, isOwner]);

  async function update(patch: { staffNote?: string | null; block?: boolean }, ok: string) {
    setBusy(true);
    setNoteError(undefined);
    try {
      onChanged(await api.staff.updateCustomer(customer.id, patch));
      toast(ok, 'success');
    } catch (e) {
      const f = fieldErrors(e);
      if (f.staffNote) setNoteError(f.staffNote);
      else toast(message(e), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open title={customer.fullName ?? 'ลูกค้า'} onClose={onClose}>
      <div className="stack">
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {customer.lineUserId ? <Chip>มี LINE</Chip> : <Chip tone="muted">ไม่มี LINE</Chip>}
          {customer.noShowCount > 0 && <Chip tone="accent">เคยไม่มา {customer.noShowCount} ครั้ง</Chip>}
          {customer.blocked && <Chip tone="accent">ถูกระงับการจองออนไลน์</Chip>}
        </div>
        {customer.phone && <p>โทร: <a href={`tel:${customer.phone}`}>{customer.phone}</a></p>}
        {isOwner && (
          <>
            <Field label="โน้ตสำหรับพนักงาน" error={noteError} hint="ลูกค้าไม่เห็นข้อความนี้">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
            </Field>
            <Button variant="secondary" loading={busy} onClick={() => void update({ staffNote: note.trim() || null }, 'บันทึกโน้ตแล้ว')}>บันทึกโน้ต</Button>
            {customer.blocked
              ? <Button variant="secondary" disabled={busy} onClick={() => void update({ block: false }, 'ยกเลิกการระงับแล้ว')}>ยกเลิกการระงับ</Button>
              : <Button variant="ghost" disabled={busy} onClick={() => void update({ block: true }, 'ระงับการจองออนไลน์แล้ว')}>ระงับการจองออนไลน์</Button>}
            <p className="section-title">ประวัติการจอง</p>
            {history === null ? <Loading /> : history.length === 0 ? <p className="muted small">ยังไม่มีประวัติ</p> : (
              <div className="list">
                {history.map((h) => (
                  <div key={h.id} className="row-between small">
                    <span>{thaiDateTime(h.startAt)} · {h.serviceName} · ช่าง{h.barberName}</span>
                    <Chip tone={STATUS_LABEL[h.status].tone}>{STATUS_LABEL[h.status].label}</Chip>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Sheet>
  );
}
